import React, { useState } from 'react';
import {
    Box, Button, Grid, TextField, MenuItem, Typography, CircularProgress
} from '@mui/material';
import { CalendarMonth as CalendarIcon } from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import PrintAgencyHeader, { formatPrintDay, formatMonthYear } from '../components/PrintAgencyHeader';
import { academyStore } from '../services/academyStore';
import { academyApi } from '../services/academyApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';

// boubaker 23/08/2026 - Page d'impression du planning des séances
// (GenericPage type 'print' : filtres masquables + impression de la zone résultats)
export const PrintPlanningPage: React.FC = () => {
    const { t } = useLanguage();

    const todayStr = new Date().toISOString().slice(0, 10);
    const monthStart = todayStr.slice(0, 8) + '01';
    const [dateFrom, setDateFrom] = useState(monthStart);
    const [dateTo, setDateTo] = useState(todayStr);
    const [roomId, setRoomId] = useState<number | 'all'>('all');
    const [teacherId, setTeacherId] = useState<number | 'all'>('all');
    const [groupId, setGroupId] = useState<number | 'all'>('all');

    const [sessions, setSessions] = useState<any[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);

    const rooms = academyStore.getRooms();
    const teachers = academyStore.getTeachers();
    const groups = academyStore.getGroups();

    const handleLoad = async () => {
        setLoading(true);
        try {
            // boubaker 23/08/2026 - chargement serveur dédié impression (DLL Blobtic_Print : données JOINées)
            const res = await academyApi.getPrintPlanning(dateFrom, dateTo, {
                room_id: roomId, teacher_id: teacherId, group_id: groupId
            });
            setSessions(res || []);
            setLoaded(true);
        } finally {
            setLoading(false);
        }
    };

    const filters = (
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2.2}>
                <TextField type="date" fullWidth size="small" label={t('academy.scheduler.start_date')} value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.2}>
                <TextField type="date" fullWidth size="small" label={t('academy.scheduler.end_date')} value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
                <TextField select fullWidth size="small" label={t('academy.scheduler.group_by_room')} value={roomId}
                    onChange={(e) => setRoomId(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
                    <MenuItem value="all">{t('academy.scheduler.all_rooms')}</MenuItem>
                    {rooms.map(r => (<MenuItem key={r.id} value={r.id}>{r.room_number}</MenuItem>))}
                </TextField>
            </Grid>
            <Grid item xs={12} sm={4} md={2.3}>
                <TextField select fullWidth size="small" label={t('academy.scheduler.group_by_teacher')} value={teacherId}
                    onChange={(e) => setTeacherId(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
                    <MenuItem value="all">{t('academy.scheduler.all_teachers')}</MenuItem>
                    {teachers.map(tt => (<MenuItem key={tt.id} value={tt.id}>{tt.full_name}</MenuItem>))}
                </TextField>
            </Grid>
            <Grid item xs={12} sm={4} md={2.3}>
                <TextField select fullWidth size="small" label={t('academy.scheduler.group_by_group')} value={groupId}
                    onChange={(e) => setGroupId(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
                    <MenuItem value="all">{t('academy.scheduler.all_groups')}</MenuItem>
                    {groups.map(g => (<MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>))}
                </TextField>
            </Grid>
            <Grid item xs={12} md={1}>
                <Button variant="contained" color="primary" fullWidth disabled={loading} onClick={handleLoad}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}>
                    {t('academy.scheduler.load')}
                </Button>
            </Grid>
        </Grid>
    );

    const cellStyle = (width?: number, extra?: React.CSSProperties): React.CSSProperties =>
        ({ border: '1px solid #999', padding: '3px 5px', textAlign: 'left', width, ...extra });

    return (
        <GenericPage
            title={t('academy.print.planning')}
            icon={<CalendarIcon />}
            type="print"
            filters={filters}
            footerEnabled={false}
        >
            {!loaded ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                    {t('academy.scheduler.not_loaded')}
                </Typography>
            ) : (
                <>
                    {/* Titre "Planning Août 2026" dérivé de la plage chargée */}
                    <PrintAgencyHeader title={`${formatMonthYear(dateFrom)}`} />

                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f0f0f0' }}>
                                <th style={cellStyle(110)}>{t('academy.scheduler.print.col_day')}</th>
                                <th style={cellStyle(60)}>{t('academy.scheduler.print.col_start')}</th>
                                <th style={cellStyle(60)}>{t('academy.scheduler.print.col_end')}</th>
                                <th style={cellStyle()}>{t('academy.scheduler.print.col_room')}</th>
                                <th style={cellStyle()}>{t('academy.scheduler.print.col_teacher')}</th>
                                <th style={cellStyle()}>{t('academy.scheduler.print.col_group')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sessions.length === 0 ? (
                                <tr><td colSpan={6} style={{ ...cellStyle(), textAlign: 'center' }}>{t('academy.print.no_data')}</td></tr>
                            ) : [...sessions]
                                .sort((a, b) => String(a.start_hour).localeCompare(String(b.start_hour)))
                                .map(s => (
                                    <tr key={s.session_id ?? `${s.start_hour}-${s.room_number}`}>
                                        <td style={cellStyle(110, { fontWeight: 'bold' })}>{formatPrintDay(String(s.start_hour).slice(0, 10))}</td>
                                        <td style={cellStyle(60)}>{String(s.start_hour).slice(11, 16)}</td>
                                        <td style={cellStyle(60)}>{String(s.end_hour).slice(11, 16)}</td>
                                        <td style={cellStyle()}>{s.room_number || ''}</td>
                                        <td style={cellStyle()}>{s.teacher_name || ''}</td>
                                        <td style={cellStyle()}>{s.group_name || ''}</td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </>
            )}
        </GenericPage>
    );
};

export default PrintPlanningPage;
